<template>
    <expandable-panel
        v-if="hasBookmarks"
        :expand="expand"
        :id="`notes-expansion-panel-wrapper-${sectionId}`"
        class="notes-expansion-panel-wrapper border"
        outlined
        @changeExpansion="changeExpansion"
    >
        <template v-slot:title>
            <div style="display: inline">
                <span class="text--secondary font-weight-bold">
                    NOTES ({{ bookmarksCount }})
                </span>
                <template v-if="displaySortButton">
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                class="no-print"
                                style="float: right;"
                                small
                                depressed
                                v-bind="attrs"
                                v-on="on"
                                @click.stop="toggleSortOrder">
                            <v-icon class="mr-1" small>mdi-sort-variant</v-icon>
                            {{ sortOrder === 'desc' ? 'Newest First' : 'Oldest First' }}
                            </v-btn>
                        </template>
                        <span class="tooltip-content">Reverse sort direction</span>
                    </v-tooltip>
                </template>
            </div>
        </template>

        <template v-slot:content>
            <v-btn
                v-if="showAllBtn && sortOrder === 'asc'"
                class="mb-2 no-print"
                depressed
                small
                block
                @click="showAllBookmarks">
                Show All ({{ bookmarksCount }})
            </v-btn>
            <v-card
                v-for="item in sortedBookmarks"
                :key="item.id"
                :id="'bookmark-list-item-' + item.id"
                flat
            >
                <v-card-text class="pa-0 notes-bookmarks-section">
                    <note-list-item
                        :showNoteControlMenu="showNoteControlMenu"
                        :type="bookmarkType(item)"
                        :data="item"
                        :tags="tags"
                    />
                </v-card-text>
            </v-card>
            <v-btn
                class="no-print"
                v-if="showAllBtn && sortOrder === 'desc'"
                depressed
                small
                block
                @click="showAllBookmarks">
                Show All ({{ bookmarksCount }})
            </v-btn>
        </template>
    </expandable-panel>
</template>

<script>
import {mapGetters, mapActions } from 'vuex';

import ExpandablePanel from "../titles/partials/ExpandablePanel.vue";
import NoteListItem from "../titles/partials/noteListItem.vue";

export default {
    name: "ContentSectionUserBookmarks",
    props: {
        bookmarks: {
            type: Array,
            required: true
        },
        tags: {
            type: Array,
            required: true
        },
        sectionId: {
            type: Number,
            default: null
        },
        expandSection: {
            type: Boolean,
            default: false
        },
        defaultShowAll: {
            type: Boolean,
            default: false
        },
        showNoteControlMenu: {
            type: Boolean,
            default: true
        },
        defaultSortOrder: {
            type: String,
            default: 'desc'
        }
    },
    components: { NoteListItem, ExpandablePanel },
    data() {
        return {
            expand: this.expandSection,
            localBookmarks: [...this.bookmarks],
            defaultBookmarksDisplay: 3,
            sortOrder: 'desc',
            showAll: false,
            displaySortButton: this.expandSection
        };
    },
    watch: {
        bookmarks(newVal) {
            this.localBookmarks = [...newVal];
        }
    },
    computed: {
        ...mapGetters('NotesBookmarksModule', [
            'getTotalNotesBookmarks'
        ]),
        hasBookmarks() {
            return this.localBookmarks.length > 0;
        },
        bookmarksCount() {
            return this.localBookmarks.length;
        },
        sortedBookmarks() {
            const sorted = [...this.localBookmarks].sort((a, b) => {
                const dateA = new Date(a.created_date);
                const dateB = new Date(b.created_date);
                return this.sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
            });
            if (this.showAll) {
                return sorted;
            } else {
                // We always show the most recent, this way when a new note is added it is always visible.
                if (this.sortOrder === 'desc') {
                    return sorted.slice(0, this.defaultBookmarksDisplay);
                } else {
                    return sorted.slice(-this.defaultBookmarksDisplay);
                }
            }
        },
        showAllBtn() {
            return this.bookmarksCount > this.defaultBookmarksDisplay && !this.showAll;
        }
    },
    methods: {
        ...mapActions('NotesBookmarksModule', [
            'updateTotalNotesBookmarks'
        ]),
        openNoteModal() {
            this.$emit("open-note-modal", { sectionId: this.sectionId });
        },
        updateBookmarks(newItems) {
            newItems.forEach(newItem => {
                const idx = this.localBookmarks.findIndex(b => b.id === newItem.id);
                if (idx !== -1) {
                    this.$set(this.localBookmarks, idx, newItem);
                } else {
                    this.localBookmarks.push(newItem);
                    //updated the notes count
                    this.updateTotalNotesBookmarks(this.getTotalNotesBookmarks + 1);
                }
            });
        },
        deleteBookmark(id) {
            this.localBookmarks = this.localBookmarks.filter(item => item.id !== id);
            //updated the notes count
            this.updateTotalNotesBookmarks(Math.max(0, this.getTotalNotesBookmarks - 1));
        },
        bookmarkType(item) {
            return item.annotation ? "note" : "bookmark";
        },
        showAllBookmarks() {
            this.showAll = true;
        },
        toggleSortOrder() {
            this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
            this.$emit('sectionSortOrderChanged', {sectionId: this.sectionId, sortOrder: this.sortOrder});
        },
        changeExpansion(val){
            this.displaySortButton = val === 0 ? true : false;
        }
    },
    created() {
        this.sortOrder = this.defaultSortOrder || this.sortOrder;
        this.showAll = this.defaultShowAll || this.showAll;
        EventBus.listen("update-notes", data => {
            if (+data.sectionId === +this.sectionId) {
                switch (data.action) {
                    case "create":
                    case "update":
                        const {
                            bookmarks = [],
                            annotations = []
                        } = data.notes || {};

                        this.updateBookmarks([
                            ...bookmarks,
                            ...annotations
                        ]);
                        break;
                    case "remove":
                        this.deleteBookmark(data.id);
                        break;
                }
            }
        });
    }
};
</script>

<style scoped lang="scss">
    .notes-expansion-panel-wrapper {
        border-left: 0 !important;
        border-right: 0 !important;
        border-radius: 0;
        padding-bottom: 6px;
        margin-top: 12px;
        margin-bottom: 12px;
    }
</style>